import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UsersRound } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { SelectField, TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { useAcademicYearPicker } from '@/hooks/usePickers';
import { transportEnrollmentsApi, transportRoutesApi } from '@/services/api/endpoints/transport';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { StudentPicker } from '@/features/finance/StudentPicker';
import { PERMISSIONS } from '@/types/permissions';
import type { UUID } from '@/types/domain';
import type {
  RouteDirection,
  TransportEnrollment,
  TransportEnrollmentStatus,
  TransportRoute,
} from '@/types/transport';
import { formatDate, orDash } from '@/utils/format';
import { EnrollmentStatusBadge, Money, formatTime, humanise, today } from './shared';

const STATUSES: { value: TransportEnrollmentStatus; label: string }[] = [
  { value: 'PENDING', label: 'Seat held' },
  { value: 'ACTIVE', label: 'Riding' },
  { value: 'SUSPENDED', label: 'Suspended' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'COMPLETED', label: 'Completed' },
];

const DIRECTIONS: { value: RouteDirection; label: string }[] = [
  { value: 'BOTH', label: 'Both ways' },
  { value: 'PICKUP', label: 'Pickup only' },
  { value: 'DROPOFF', label: 'Drop-off only' },
];

/** The route/stop halves of an enrolment form — shared by enrol and move. */
interface ServiceForm {
  direction: RouteDirection;
  pickupRouteId: string;
  pickupStopId: string;
  dropOffRouteId: string;
  dropOffStopId: string;
}

const EMPTY_SERVICE: ServiceForm = {
  direction: 'BOTH',
  pickupRouteId: '',
  pickupStopId: '',
  dropOffRouteId: '',
  dropOffStopId: '',
};

/** Only the halves the direction asks for; a stop rides with its route. */
function serviceHalves(service: ServiceForm) {
  return {
    direction: service.direction,
    ...(service.direction !== 'DROPOFF' && service.pickupRouteId !== ''
      ? {
          pickupRouteId: service.pickupRouteId,
          ...(service.pickupStopId !== '' ? { pickupStopId: service.pickupStopId } : {}),
        }
      : {}),
    ...(service.direction !== 'PICKUP' && service.dropOffRouteId !== ''
      ? {
          dropOffRouteId: service.dropOffRouteId,
          ...(service.dropOffStopId !== '' ? { dropOffStopId: service.dropOffStopId } : {}),
        }
      : {}),
  };
}

/**
 * Which students ride, where they board, and what the family pays.
 *
 * <p>An enrolment starts PENDING — a seat is held and nothing is billed. Money
 * never has its own screen here: activating creates the finance fee
 * assignment, suspending pauses it, cancelling ends it, and the `billing`
 * caption under each fee is what finance says the assignment currently is —
 * shown, never worked out again in the browser.
 *
 * <p>Capacity is the same story. The route options quote the server's own
 * available-seat arithmetic, and an over-full route is refused by the server
 * with the reason spelled out, not greyed out by a rule duplicated here.
 */
export function EnrollmentsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('studentName,asc');
  const manages = can(PERMISSIONS.TRANSPORT_ENROLLMENT_MANAGE);
  const viewsRoutes = can(PERMISSIONS.TRANSPORT_ROUTE_VIEW);

  const [status, setStatus] = useState<TransportEnrollmentStatus | ''>('');
  const [yearId, setYearId] = useState('');
  const [routeId, setRouteId] = useState('');

  const [enrolOpen, setEnrolOpen] = useState(false);
  const [student, setStudent] = useState<{ id: UUID; name: string; number?: string } | null>(null);
  const [enrolService, setEnrolService] = useState<ServiceForm>(EMPTY_SERVICE);
  const [enrolForm, setEnrolForm] = useState({
    academicYearId: '',
    startDate: today(),
    monthlyFee: '',
    registrationFee: '',
    specialInstructions: '',
  });

  const [moving, setMoving] = useState<TransportEnrollment | null>(null);
  const [moveService, setMoveService] = useState<ServiceForm>(EMPTY_SERVICE);
  const [moveForm, setMoveForm] = useState({ monthlyFee: '', specialInstructions: '' });

  const years = useAcademicYearPicker();

  const params = {
    ...table.params,
    ...(status !== '' ? { status } : {}),
    ...(yearId !== '' ? { academicYearId: yearId } : {}),
    ...(routeId !== '' ? { routeId } : {}),
  };

  const enrollments = useQuery({
    queryKey: queryKeys.transportEnrollments.list(params),
    queryFn: () => transportEnrollmentsApi.list(params),
  });

  /* One route list feeds the filter and both modals' cascades. The seat
     figures in its option labels are the server's arithmetic, quoted. */
  const routes = useQuery({
    queryKey: queryKeys.transportRoutes.list({ picker: true }),
    queryFn: () => transportRoutesApi.list({ page: 0, size: 200 }),
    enabled: viewsRoutes,
    staleTime: 5 * 60_000,
  });
  const routeList = routes.data?.data ?? [];

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.transportEnrollments.all });
    // Every status move shifts a route's seat arithmetic, and most touch a
    // finance assignment and a dashboard count.
    void queryClient.invalidateQueries({ queryKey: queryKeys.transportRoutes.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.transport.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.feeAssignments.all });
  };

  const enrol = useMutation({
    mutationFn: () =>
      transportEnrollmentsApi.enrol({
        studentId: student!.id,
        ...(enrolForm.academicYearId !== '' ? { academicYearId: enrolForm.academicYearId } : {}),
        ...serviceHalves(enrolService),
        ...(enrolForm.startDate !== '' ? { startDate: enrolForm.startDate } : {}),
        ...(enrolForm.monthlyFee !== '' ? { monthlyFee: Number(enrolForm.monthlyFee) } : {}),
        ...(enrolForm.registrationFee !== ''
          ? { registrationFee: Number(enrolForm.registrationFee) }
          : {}),
        ...(enrolForm.specialInstructions.trim() !== ''
          ? { specialInstructions: enrolForm.specialInstructions.trim() }
          : {}),
      }),
    onSuccess: (row) => {
      toast.success(`${row.studentName} enrolled — the seat is held until activation`);
      closeEnrol();
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const activate = useMutation({
    mutationFn: (id: string) => transportEnrollmentsApi.activate(id),
    onSuccess: (row) => {
      toast.success(`${row.studentName} rides from ${formatDate(row.startDate)}`);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const suspend = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      transportEnrollmentsApi.suspend(id, reason),
    onSuccess: (row) => {
      toast.success(`${row.studentName}'s service is suspended — the seat stays held`);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const cancel = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      transportEnrollmentsApi.cancel(id, reason),
    onSuccess: (row) => {
      toast.info(`${row.studentName}'s enrolment is cancelled — the seat frees`);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const move = useMutation({
    mutationFn: () =>
      transportEnrollmentsApi.changeStops(moving!.id, {
        ...serviceHalves(moveService),
        ...(moveForm.monthlyFee !== '' ? { monthlyFee: Number(moveForm.monthlyFee) } : {}),
        ...(moveForm.specialInstructions.trim() !== ''
          ? { specialInstructions: moveForm.specialInstructions.trim() }
          : {}),
      }),
    onSuccess: (row) => {
      toast.success(`${row.studentName}'s service updated`);
      setMoving(null);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  function closeEnrol() {
    setEnrolOpen(false);
    setStudent(null);
    setEnrolService(EMPTY_SERVICE);
    setEnrolForm({
      academicYearId: '',
      startDate: today(),
      monthlyFee: '',
      registrationFee: '',
      specialInstructions: '',
    });
  }

  function openMove(row: TransportEnrollment) {
    setMoving(row);
    setMoveService({
      direction: row.direction,
      pickupRouteId: row.pickupRouteId ?? '',
      pickupStopId: row.pickupStopId ?? '',
      dropOffRouteId: row.dropOffRouteId ?? '',
      dropOffStopId: row.dropOffStopId ?? '',
    });
    setMoveForm({
      monthlyFee: row.monthlyFee === undefined ? '' : String(row.monthlyFee),
      specialInstructions: row.specialInstructions ?? '',
    });
  }

  const columns: Column<TransportEnrollment>[] = [
    {
      key: 'student',
      header: 'Student',
      sortKey: 'studentName',
      render: (row) => (
        <span>
          <span className="block text-sm font-semibold text-ink">{row.studentName}</span>
          <span className="block font-mono text-[11px] text-subtle">
            {orDash(row.studentNumber)}
          </span>
        </span>
      ),
    },
    {
      key: 'year',
      header: 'Year',
      render: (row) => orDash(row.academicYearName),
    },
    {
      key: 'service',
      header: 'Rides',
      render: (row) => (
        <span className="text-[12px]">
          <span className="block text-ink">{humanise(row.direction)}</span>
          {row.direction !== 'DROPOFF' && (
            <span className="block text-[11px] text-subtle">
              up {orDash(row.pickupStopName)} · {orDash(row.pickupRouteName)}
            </span>
          )}
          {row.direction !== 'PICKUP' && (
            <span className="block text-[11px] text-subtle">
              down {orDash(row.dropOffStopName)} · {orDash(row.dropOffRouteName)}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'fee',
      header: 'Monthly fee',
      className: 'text-right',
      render: (row) => (
        <span>
          <Money value={row.monthlyFee} />
          {/* Finance's word on the billing, never recomputed here. */}
          {row.feeAssignmentStatus !== undefined && (
            <span className="block text-[11px] text-subtle">
              billing {humanise(row.feeAssignmentStatus)}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'startDate',
      header: 'From',
      sortKey: 'startDate',
      render: (row) => (
        <span className="whitespace-nowrap text-[13px]">
          <span className="tabular-nums text-ink">{formatDate(row.startDate)}</span>
          <span className="block text-[11px] text-subtle">
            {row.endDate === undefined ? 'ongoing' : `until ${formatDate(row.endDate)}`}
          </span>
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <span>
          <EnrollmentStatusBadge status={row.status} />
          {row.statusReason !== undefined && (
            <span className="mt-0.5 block max-w-[16rem] truncate text-[11px] text-subtle" title={row.statusReason}>
              {row.statusReason}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (row) => {
        if (!manages || row.status === 'CANCELLED' || row.status === 'COMPLETED') {
          return null;
        }
        return (
          <div className="flex flex-wrap justify-end gap-1">
            {(row.status === 'PENDING' || row.status === 'SUSPENDED') && (
              <Button
                size="sm"
                variant="secondary"
                disabled={activate.isPending}
                onClick={() => activate.mutate(row.id)}
              >
                {row.status === 'PENDING' ? 'Activate' : 'Resume'}
              </Button>
            )}
            {row.status === 'ACTIVE' && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  const reason = window.prompt('Why is the service being suspended?');
                  if (reason !== null && reason.trim() !== '') {
                    suspend.mutate({ id: row.id, reason: reason.trim() });
                  }
                }}
              >
                Suspend
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => openMove(row)}>
              Move
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                const reason = window.prompt('Why is the enrolment being cancelled?');
                if (reason !== null && reason.trim() !== '') {
                  cancel.mutate({ id: row.id, reason: reason.trim() });
                }
              }}
            >
              Cancel
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <ListPage
        title="Riders"
        description="Who is on the bus this year, and what each family pays."
        icon={<UsersRound className="h-5 w-5" aria-hidden="true" />}
        action={
          manages ? <Button onClick={() => setEnrolOpen(true)}>Enrol a student</Button> : undefined
        }
        search={{
          value: table.searchInput,
          onChange: table.setSearchInput,
          placeholder: 'Search by student name or number…',
        }}
        filters={
          <>
            <FilterSelect
              label="Status"
              value={status}
              onChange={(value) => {
                setStatus(value);
                table.setPage(0);
              }}
              options={STATUSES}
            />
            <FilterSelect
              label="Year"
              value={yearId}
              onChange={(value) => {
                setYearId(value);
                table.setPage(0);
              }}
              options={(years.data ?? []).map((year) => ({ value: year.id, label: year.name }))}
            />
            {viewsRoutes && (
              <FilterSelect
                label="Route"
                value={routeId}
                onChange={(value) => {
                  setRouteId(value);
                  table.setPage(0);
                }}
                options={routeList.map((route) => ({ value: route.id, label: route.displayName }))}
              />
            )}
          </>
        }
        pagination={enrollments.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={enrollments.data?.data}
          rowKey={(row) => row.id}
          loading={enrollments.isPending}
          error={enrollments.error}
          onRetry={() => void enrollments.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle="Nobody rides yet"
          emptyDescription="Enrol a student to hold them a seat on a route."
          emptyIcon={<UsersRound className="h-6 w-6" aria-hidden="true" />}
        />
      </ListPage>

      <Modal
        open={enrolOpen}
        title="Enrol a student"
        description="Starts pending: the seat is held and nothing is billed until the service is activated."
        onClose={closeEnrol}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={closeEnrol}>
              Cancel
            </Button>
            <Button
              loading={enrol.isPending}
              disabled={student === null}
              onClick={() => enrol.mutate()}
            >
              Hold the seat
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <StudentPicker value={student} onChange={setStudent} />

          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              label="Academic year"
              value={enrolForm.academicYearId}
              hint="Leave as-is for the current year."
              onChange={(event) =>
                setEnrolForm((form) => ({ ...form, academicYearId: event.target.value }))
              }
            >
              <option value="">Current year</option>
              {(years.data ?? []).map((year) => (
                <option key={year.id} value={year.id}>
                  {year.name}
                </option>
              ))}
            </SelectField>
            <TextField
              label="Rides from"
              type="date"
              value={enrolForm.startDate}
              onChange={(event) =>
                setEnrolForm((form) => ({ ...form, startDate: event.target.value }))
              }
            />
          </div>

          <ServiceFields routes={routeList} value={enrolService} onChange={setEnrolService} />

          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Monthly fee"
              type="number"
              min={0}
              step="0.01"
              value={enrolForm.monthlyFee}
              hint="Zero means the school carries the cost — no fee assignment is created."
              onChange={(event) =>
                setEnrolForm((form) => ({ ...form, monthlyFee: event.target.value }))
              }
            />
            <TextField
              label="Registration fee"
              type="number"
              min={0}
              step="0.01"
              value={enrolForm.registrationFee}
              hint="A one-time charge, invoiced immediately. Leave blank for none."
              onChange={(event) =>
                setEnrolForm((form) => ({ ...form, registrationFee: event.target.value }))
              }
            />
          </div>

          <TextField
            label="Special instructions"
            value={enrolForm.specialInstructions}
            hint="Shown to the crew on every trip manifest."
            onChange={(event) =>
              setEnrolForm((form) => ({ ...form, specialInstructions: event.target.value }))
            }
          />
        </div>
      </Modal>

      <Modal
        open={moving !== null}
        title={moving === null ? 'Move the service' : `Move ${moving.studentName}`}
        description="The routine mid-year change of routes, stops or fee. Joining a new route re-checks its capacity on the server."
        onClose={() => setMoving(null)}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setMoving(null)}>
              Cancel
            </Button>
            <Button loading={move.isPending} onClick={() => move.mutate()}>
              Save the move
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <ServiceFields routes={routeList} value={moveService} onChange={setMoveService} />

          <TextField
            label="Monthly fee"
            type="number"
            min={0}
            step="0.01"
            value={moveForm.monthlyFee}
            hint="A changed fee ends the old assignment and starts a new one — billed history stays whole."
            onChange={(event) =>
              setMoveForm((form) => ({ ...form, monthlyFee: event.target.value }))
            }
          />
          <TextField
            label="Special instructions"
            value={moveForm.specialInstructions}
            onChange={(event) =>
              setMoveForm((form) => ({ ...form, specialInstructions: event.target.value }))
            }
          />
        </div>
      </Modal>
    </>
  );
}

/**
 * Direction, then a route and stop per half — the cascade both modals share.
 *
 * The option labels quote the server's seat arithmetic; nothing is greyed out
 * by a rule of our own. A full or inactive route is the server's refusal to
 * make, with the reason in its words.
 */
function ServiceFields({
  routes,
  value,
  onChange,
}: {
  routes: TransportRoute[];
  value: ServiceForm;
  onChange: (value: ServiceForm) => void;
}) {
  const pickupStops = useQuery({
    queryKey: queryKeys.transportRoutes.stops(value.pickupRouteId),
    queryFn: () => transportRoutesApi.stops(value.pickupRouteId),
    enabled: value.direction !== 'DROPOFF' && value.pickupRouteId !== '',
  });

  const dropOffStops = useQuery({
    queryKey: queryKeys.transportRoutes.stops(value.dropOffRouteId),
    queryFn: () => transportRoutesApi.stops(value.dropOffRouteId),
    enabled: value.direction !== 'PICKUP' && value.dropOffRouteId !== '',
  });

  const routeLabel = (route: TransportRoute) =>
    `${route.displayName} · ${route.availableSeats} of ${route.effectiveCapacity} seats free`;

  return (
    <div className="space-y-4">
      <SelectField
        label="Direction"
        required
        value={value.direction}
        onChange={(event) =>
          onChange({ ...value, direction: event.target.value as RouteDirection })
        }
      >
        {DIRECTIONS.map((direction) => (
          <option key={direction.value} value={direction.value}>
            {direction.label}
          </option>
        ))}
      </SelectField>

      {value.direction !== 'DROPOFF' && (
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            label="Pickup route"
            required
            value={value.pickupRouteId}
            onChange={(event) =>
              onChange({ ...value, pickupRouteId: event.target.value, pickupStopId: '' })
            }
          >
            <option value="">Choose…</option>
            {routes
              .filter((route) => route.status === 'ACTIVE' && route.direction !== 'DROPOFF')
              .map((route) => (
                <option key={route.id} value={route.id}>
                  {routeLabel(route)}
                </option>
              ))}
          </SelectField>
          <SelectField
            label="Pickup stop"
            required
            value={value.pickupStopId}
            disabled={value.pickupRouteId === ''}
            onChange={(event) => onChange({ ...value, pickupStopId: event.target.value })}
          >
            <option value="">
              {value.pickupRouteId === '' ? 'Choose a route first' : 'Choose…'}
            </option>
            {(pickupStops.data ?? [])
              .filter((stop) => stop.allowsPickup && stop.status === 'ACTIVE')
              .map((stop) => (
                <option key={stop.id} value={stop.id}>
                  {stop.sequence}. {stop.name} · {formatTime(stop.pickupTime)}
                </option>
              ))}
          </SelectField>
        </div>
      )}

      {value.direction !== 'PICKUP' && (
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            label="Drop-off route"
            required
            value={value.dropOffRouteId}
            onChange={(event) =>
              onChange({ ...value, dropOffRouteId: event.target.value, dropOffStopId: '' })
            }
          >
            <option value="">Choose…</option>
            {routes
              .filter((route) => route.status === 'ACTIVE' && route.direction !== 'PICKUP')
              .map((route) => (
                <option key={route.id} value={route.id}>
                  {routeLabel(route)}
                </option>
              ))}
          </SelectField>
          <SelectField
            label="Drop-off stop"
            required
            value={value.dropOffStopId}
            disabled={value.dropOffRouteId === ''}
            onChange={(event) => onChange({ ...value, dropOffStopId: event.target.value })}
          >
            <option value="">
              {value.dropOffRouteId === '' ? 'Choose a route first' : 'Choose…'}
            </option>
            {(dropOffStops.data ?? [])
              .filter((stop) => stop.allowsDropOff && stop.status === 'ACTIVE')
              .map((stop) => (
                <option key={stop.id} value={stop.id}>
                  {stop.sequence}. {stop.name} · {formatTime(stop.dropOffTime)}
                </option>
              ))}
          </SelectField>
        </div>
      )}
    </div>
  );
}
